import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(
    private meta: Meta, 
    private titleService: Title,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  updateMeta(title: string, description: string, image: string, url: string, keywords: string = '') {
    // Set page title
    this.titleService.setTitle(title);

    // Basic meta tags
    this.meta.updateTag({ name: 'description', content: description });
    
    // Ensure image URL is absolute
    const absoluteImageUrl = this.ensureAbsoluteUrl(image);

    // Clear existing tags to avoid conflicts
    this.clearExistingMetaTags();

    // SOLUTION 1: Open Graph avec dimensions flexibles pour affichage complet
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: absoluteImageUrl });
    this.meta.updateTag({ property: 'og:image:secure_url', content: absoluteImageUrl });
    
    // NE PAS fixer de dimensions spécifiques - laisse les réseaux sociaux utiliser les dimensions originales
    // this.meta.updateTag({ property: 'og:image:width', content: '1200' });
    // this.meta.updateTag({ property: 'og:image:height', content: '630' });
    
    this.meta.updateTag({ property: 'og:image:type', content: 'image/jpeg' });
    this.meta.updateTag({ property: 'og:image:alt', content: title });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:type', content: 'article' });
    this.meta.updateTag({ property: 'og:site_name', content: 'AfroTech Hub' });
    this.meta.updateTag({ property: 'og:locale', content: 'fr_FR' });
    this.meta.updateTag({ property: 'fb:app_id', content: '910541497834748' });

    // SOLUTION 2: Twitter Card avec summary au lieu de summary_large_image
    // pour garder l'image complète visible
    this.meta.updateTag({ name: 'twitter:card', content: 'summary' }); // Au lieu de summary_large_image
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: absoluteImageUrl });
    this.meta.updateTag({ name: 'twitter:image:alt', content: title });
    this.meta.updateTag({ name: 'twitter:site', content: '@afrotech_hub' });
    this.meta.updateTag({ name: 'twitter:creator', content: '@afrotech_hub' });

    // LinkedIn spécifique
    this.meta.updateTag({ property: 'article:author', content: 'AfroTech Hub' });

    // Additional meta tags
    this.meta.updateTag({ name: 'author', content: 'AfroTech Hub' });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ httpEquiv: 'Content-Type', content: 'text/html; charset=utf-8' });

    // Canonical URL
    this.updateCanonicalUrl(url);

    this.meta.updateTag({ name: 'keywords', content: keywords ? keywords.split(';').map(k => k.trim()).join(', ') : ''});
    
    // Vitesse et performance
    this.meta.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1.0, viewport-fit=cover' });
    
    // Sécurité
    //this.meta.updateTag({ httpEquiv: 'Content-Security-Policy', content: "default-src 'self'" });

    // Schema.org structured data
    this.updateStructuredData(title, description, absoluteImageUrl, url);

  }

  // SOLUTION 3: Méthode spéciale pour créer une image de partage optimisée
  updateMetaWithFullImageDisplay(title: string, description: string, image: string, url: string, keywords: string) {
    // Créer une version de l'image avec padding pour qu'elle s'affiche entière
    const processedImageUrl = this.createFullDisplayImage(image);
    
    this.updateMeta(title, description, processedImageUrl, url, keywords);
  }

  // SOLUTION 4: Créer une image avec padding pour l'affichage complet
  private createFullDisplayImage(originalImageUrl: string): string {
    // Si vous avez un service de traitement d'image (comme Cloudinary)
    // Exemple avec Cloudinary pour ajouter du padding et garder l'image complète :
    
    const absoluteUrl = this.ensureAbsoluteUrl(originalImageUrl);
    
    // Exemple avec Cloudinary (remplacez par votre service) :
    // return `https://res.cloudinary.com/your-cloud/image/fetch/w_1200,h_630,c_pad,b_white/${encodeURIComponent(absoluteUrl)}`;
    
    // Si vous utilisez un autre service ou votre propre API :
    // return `https://afrotech-hub.com/api/images/social-share?url=${encodeURIComponent(absoluteUrl)}&width=1200&height=630&fit=contain`;
    
    return absoluteUrl;
  }

  private clearExistingMetaTags(): void {
    // Remove existing og tags
    const existingOgTags = [
      'og:title', 'og:description', 'og:image', 'og:image:secure_url',
      'og:image:width', 'og:image:height', 'og:image:type', 'og:url',
      'og:type', 'og:site_name', 'og:image:alt', 'og:locale'
    ];

    existingOgTags.forEach(tag => {
      const existingTag = this.meta.getTag(`property="${tag}"`);
      if (existingTag) {
        this.meta.removeTag(`property="${tag}"`);
      }
    });

    // Clear Twitter tags
    const twitterTags = [
      'twitter:card', 'twitter:title', 'twitter:description', 
      'twitter:image', 'twitter:image:alt', 'twitter:site', 'twitter:creator'
    ];

    twitterTags.forEach(tag => {
      const existingTag = this.meta.getTag(`name="${tag}"`);
      if (existingTag) {
        this.meta.removeTag(`name="${tag}"`);
      }
    });
  }

  private updateCanonicalUrl(url: string): void {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = url;
    document.head.appendChild(link);
  }

  private updateStructuredData(title: string, description: string, image: string, url: string): void {
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Structured data sans dimensions fixes pour l'image
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": title,
      "description": description,
      "image": {
        "@type": "ImageObject",
        "url": image
        // Pas de width/height fixes pour permettre l'affichage original
      },
      "url": url,
      "datePublished": new Date().toISOString(),
      "dateModified": new Date().toISOString(),
      "author": {
        "@type": "Organization",
        "name": "AfroTech Hub",
        "url": "https://afrotech-hub.com"
      },
      "publisher": {
        "@type": "Organization",
        "name": "AfroTech Hub",
        "logo": {
          "@type": "ImageObject",
          "url": "https://afrotech-hub.com/assets/images/logo.png"
        },
        "url": "https://afrotech-hub.com"
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  private ensureAbsoluteUrl(url: string): string {
    if (!url) return 'https://afrotech-hub.com/assets/images/afrotech-talent-cover.jpg';
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    if (url.startsWith('/')) {
      return `https://afrotech-hub.com${url}`;
    }
    
    return `https://afrotech-hub.com/${url}`;
  }

  // SOLUTION 5: Méthode pour détecter le ratio de l'image et adapter les meta tags
  async updateMetaWithImageAnalysis(title: string, description: string, image: string, url: string, keywords: string) {
    const absoluteImageUrl = this.ensureAbsoluteUrl(image);
    
    if (isPlatformBrowser(this.platformId)) {
      try {
        const dimensions = await this.getImageDimensions(absoluteImageUrl);
        
        // Si l'image est carrée ou portrait, utiliser twitter:card summary
        // Si l'image est landscape, permettre summary_large_image
        const isSquareOrPortrait = dimensions.width <= dimensions.height;
        
        this.updateMetaWithCardType(title, description, absoluteImageUrl, url, 
          isSquareOrPortrait ? 'summary' : 'summary_large_image', keywords);
      } catch (error) {
        // Fallback en cas d'erreur
        this.updateMeta(title, description, absoluteImageUrl, url, keywords);
      }
    } else {
      // Côté serveur, utiliser la configuration par défaut
      this.updateMeta(title, description, absoluteImageUrl, url, keywords);
    }
  }

  private updateMetaWithCardType(title: string, description: string, image: string, url: string, cardType: string, keywords: string = '') {
    this.titleService.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    
    this.clearExistingMetaTags();

    // Open Graph (sans dimensions fixes)
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ property: 'og:image:secure_url', content: image });
    this.meta.updateTag({ property: 'og:image:type', content: 'image/jpeg' });
    this.meta.updateTag({ property: 'og:image:alt', content: title });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:type', content: 'article' });
    this.meta.updateTag({ property: 'og:site_name', content: 'AfroTech Hub' });

    // Twitter avec le type de carte adapté
    this.meta.updateTag({ name: 'twitter:card', content: cardType });
    this.meta.updateTag({ name: 'twitter:title', content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: image });
    this.meta.updateTag({ name: 'twitter:image:alt', content: title });
    this.meta.updateTag({ name: 'twitter:site', content: '@afrotech_hub' });

    this.meta.updateTag({ name: 'keywords', content: keywords ? keywords.split(';').map(k => k.trim()).join(', ') : ''});

    // Vitesse et performance
    this.meta.updateTag({ name: 'viewport', content: 'width=device-width, initial-scale=1.0, viewport-fit=cover' });
    
    // Sécurité
    //this.meta.updateTag({ httpEquiv: 'Content-Security-Policy', content: "default-src 'self'" });
        

    this.updateCanonicalUrl(url);
    this.updateStructuredData(title, description, image, url);
  }

  private getImageDimensions(imageUrl: string): Promise<{width: number, height: number}> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = imageUrl;
    });
  }
}